import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import CTASection from "@/components/site/CTASection";

const communityGroups = [
  {
    title: "Schools and colleges",
    text:
      "Free, age-appropriate rail safety education resources for primary schools, secondary schools, sixth forms and colleges. Designed to support assemblies, classroom lessons, PSHE, citizenship, careers education and community safety campaigns.",
  },
  {
    title: "Youth organisations",
    text:
      "Practical rail safety packs for cadets, Scouts, Guides, DofE groups, youth clubs and community organisations. Resources are designed to be easy to deliver, discussion-led and suitable for young people at different stages of maturity.",
  },
  {
    title: "Parents and carers",
    text:
      "Clear rail safety guidance to help families talk about trespass, level crossings, platforms, railway electricity, social media dares, shortcuts and travelling independently by train.",
  },
  {
    title: "Aspiring rail professionals",
    text:
      "Resources for older learners exploring railway careers, helping them understand safety-critical thinking, operational judgement, personal responsibility and the standards expected across the railway industry.",
  },
];

const safetyAgeGroups = [
  {
    age: "Ages 4–7",
    title: "Stay Away, Stay Safe",
    text:
      "Simple, visual rail safety messages for younger pupils. Focuses on staying with adults, never playing near railway lines, recognising railway warning signs and understanding that railways are not places to explore.",
  },
  {
    age: "Ages 7–11",
    title: "Stop, Look, Listen, Think",
    text:
      "Primary school rail safety activities covering platforms, level crossings, railway signs, unsafe shortcuts, railway electricity and why trains cannot stop quickly.",
  },
  {
    age: "Ages 11–14",
    title: "One Bad Decision",
    text:
      "Scenario-based rail safety learning around trespass, peer pressure, dares, filming near the railway, distractions, shortcuts and the consequences of unsafe choices.",
  },
  {
    age: "Ages 14–16",
    title: "Real Risk, Real Consequences",
    text:
      "Secondary school rail safety resources exploring personal responsibility, group behaviour, social media influence, legal consequences and the effect of railway incidents on others.",
  },
  {
    age: "Ages 16–18",
    title: "Risk, Responsibility and the Railway",
    text:
      "Post-16 rail safety and careers resources linking operational judgement, employability, safety-critical behaviour, railway careers and responsible decision-making.",
  },
  {
    age: "SEND and accessible learning",
    title: "Clear, visual and repeatable",
    text:
      "Accessible rail safety resources using clear language, visual prompts, repetition and practical examples to support learners with different needs.",
  },
];

const resourceTypes = [
  "Rail safety assembly packs",
  "Teacher lesson plans",
  "Classroom worksheets",
  "Hazard-spotting activities",
  "Scenario discussion cards",
  "Rail safety quizzes",
  "Parent and carer handouts",
  "Poster campaign templates",
  "Pupil participation certificates",
  "Rail Safety Week resources",
  "Rail career pathway explainers",
  "Careers event activity packs",
  "College employability workshops",
  "Individual progression checklists",
];

const careersAudiences = [
  {
    title: "Individuals",
    text:
      "Career resources for aspiring rail professionals, career changers and post-16 learners. Includes role explainers, recruitment journey guides, behaviour expectations and practical next-step planning tools.",
    cta: "Explore individual support",
    href: "/for-individuals",
  },
  {
    title: "Schools",
    text:
      "School-friendly rail careers resources for key stages and sixth forms. Built for PSHE, citizenship and careers programmes to connect safe decision-making with employability and future pathways.",
    cta: "View school resources",
    href: "/contact",
  },
  {
    title: "Colleges",
    text:
      "College-focused resource packs linking rail safety awareness to vocational pathways, professional standards and workplace behaviours expected across railway roles.",
    cta: "Talk to UKTDA",
    href: "/contact",
  },
  {
    title: "Careers leaders and tutors",
    text:
      "Ready-to-use activity frameworks, discussion prompts and campaign ideas for careers fairs, tutorial sessions and progression coaching with rail as a high-impact sector.",
    cta: "Plan a careers session",
    href: "/contact",
  },
];

const professionalThemes = [
  {
    title: "Safety-critical thinking",
    text:
      "Helping learners understand that railway safety is not just about knowing rules. It is about judgement, behaviour, responsibility and making safe decisions under pressure.",
  },
  {
    title: "Operational awareness",
    text:
      "Introducing the idea that the railway is a controlled environment where small unsafe decisions can create serious consequences for passengers, staff, families and communities.",
  },
  {
    title: "Careers and aspiration",
    text:
      "Supporting older students and career changers to understand the behaviours expected in railway roles, including reliability, communication, risk awareness and professionalism.",
  },
  {
    title: "Community impact",
    text:
      "Creating resources that schools, parents, youth organisations and community partners can use to start meaningful conversations about railway risk and safer behaviour.",
  },
];

const faqs = [
  {
    question: "Are the UKTDA rail safety resources free for schools?",
    answer:
      "Yes. UKTDA provides free rail safety education resources for schools, colleges, youth organisations and community groups. The resources are designed to help teachers and youth leaders deliver practical railway safety messages without needing specialist railway knowledge.",
  },
  {
    question: "What age groups are the rail safety resources suitable for?",
    answer:
      "The resources are structured for different age groups, including ages 4–7, 7–11, 11–14, 14–16 and 16–18. There are also SEND and accessible learning resources using clearer language, visual prompts and repeatable messages.",
  },
  {
    question: "Can primary schools use these rail safety resources?",
    answer:
      "Yes. Primary school resources include simple rail safety messages, hazard-spotting activities, platform safety, level crossing awareness, railway warning signs and worksheets that help younger pupils understand why they must stay away from railway lines.",
  },
  {
    question: "Can secondary schools use these resources for PSHE or citizenship?",
    answer:
      "Yes. The secondary school resources are suitable for PSHE, citizenship, form-time activities, assemblies and community safety lessons. They cover railway trespass, peer pressure, social media dares, unsafe shortcuts, distraction, decision-making and real-world consequences.",
  },
  {
    question: "Do you provide rail safety resources for colleges and sixth forms?",
    answer:
      "Yes. Post-16 resources focus on risk, responsibility, railway behaviour, employability and safety-critical thinking. They are suitable for sixth forms, colleges, careers programmes and learners who may be interested in future railway careers.",
  },
  {
    question: "Can youth organisations use the resources?",
    answer:
      "Yes. The resources can be used by youth organisations including cadet units, Scouts, Guides, DofE groups, youth clubs, sports clubs and community groups. The materials are designed to be practical, discussion-led and easy to deliver in a group setting.",
  },
  {
    question: "Do teachers need railway experience to deliver the sessions?",
    answer:
      "No. The resources are designed to be classroom-ready. Teacher notes, discussion prompts, worksheets and activity guidance help schools deliver the key messages without requiring operational railway knowledge.",
  },
  {
    question: "What rail safety topics are covered?",
    answer:
      "Topics include railway trespass, level crossing safety, platform safety, railway electricity, warning signs, unsafe shortcuts, peer pressure, social media filming, distraction, travelling independently and the wider consequences of unsafe behaviour around the railway.",
  },
  {
    question: "Are the resources suitable for SEND learners?",
    answer:
      "Yes. UKTDA includes accessible rail safety resources designed around clear language, visual prompts, repetition and practical examples. These resources can support learners who benefit from simplified messages and structured reinforcement.",
  },
  {
    question: "Can schools use the resources during Rail Safety Week?",
    answer:
      "Yes. The resources can support Rail Safety Week, school assemblies, themed safety days, PSHE lessons, citizenship lessons, careers events and local community safety campaigns.",
  },
  {
    question: "Do the resources replace official railway safety advice?",
    answer:
      "No. UKTDA resources are designed to support and complement official railway safety messages. Schools and organisations should always follow official guidance from relevant railway, safety and safeguarding bodies.",
  },
  {
    question: "How do these resources link to railway careers?",
    answer:
      "For older learners, the resources introduce the importance of judgement, professionalism, risk awareness and safety-critical behaviour. These are important themes in many railway careers, including operational, safety-critical and customer-facing roles.",
  },
];

export default function Communities() {
  return (
    <>
      <SEO
        title="Rail Safety and Rail Careers Resources | UKTDA Communities"
        description="Free rail safety and rail careers resources for individuals, schools, colleges, youth organisations and community groups. Practical lesson packs, careers activities, workshops and accessible materials from UKTDA."
        path="/communities"
      />

      <PageHero
        eyebrow="UKTDA Communities"
        title="Free rail safety and rail careers resources for individuals, schools and colleges."
        intro="Use practical resources designed for different audiences and age groups, from classroom rail safety education to rail career pathway activities, employability discussions and post-16 progression support."
      />

      <section className="py-20 md:py-28 bg-white" data-testid="communities-intro">
        <div className="container-rail">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="label-eyebrow text-safety">Free rail safety education</p>
              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Schools should not have to pay to teach young people how to stay safe around the railway.
              </h2>
            </div>

            <div className="space-y-5 text-slate-600 leading-relaxed">
              <p>
                UKTDA Communities provides free rail safety education resources for schools,
                colleges, youth groups and community organisations. The resources help young
                people understand railway risk in a practical, age-appropriate and memorable way.
              </p>
              <p>
                The materials are designed for teachers, youth leaders, parents and community
                partners who want ready-to-use rail safety activities without needing specialist
                railway knowledge.
              </p>
              <p>
                From simple safety messages for younger pupils to decision-making scenarios for
                teenagers and post-16 learners, UKTDA Communities helps turn rail safety awareness
                into safer choices.
              </p>
              <p>
                We also support rail career discovery through resources for individuals, schools
                and colleges that link safety-critical behaviours with employability, progression
                and informed career decisions.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-3">
                <a
                  href="/contact"
                  data-testid="communities-intro-contact"
                  className="inline-flex items-center justify-center rounded-full bg-safety px-6 py-3 font-semibold text-navy shadow-sm hover:opacity-90 transition"
                >
                  Request free resources
                </a>
                <a
                  href="#rail-safety-faq"
                  data-testid="communities-intro-faq"
                  className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 font-semibold text-navy hover:bg-slate-50 transition"
                >
                  Read school FAQs
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="rail-careers-audiences">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Rail careers specialism</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Specialised resources for rail careers across individuals, schools and colleges.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              UKTDA Communities combines rail safety education with practical rail careers
              resources. This helps each audience access guidance that fits their role, whether
              they are making personal career decisions or delivering careers education at scale.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {careersAudiences.map((audience) => (
              <article
                key={audience.title}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-7"
              >
                <h3 className="font-display font-bold text-xl text-navy">
                  {audience.title}
                </h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{audience.text}</p>
                <a
                  href={audience.href}
                  className="mt-5 inline-flex items-center font-semibold text-navy underline decoration-safety/70 underline-offset-4 hover:opacity-80 transition"
                >
                  {audience.cta}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="communities-who-for">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Who it is for</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              A practical rail safety and rail careers resource hub for schools, families and communities.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              UKTDA Communities supports different groups with different needs: from primary
              school pupils learning basic railway safety to older students exploring risk,
              responsibility, rail employability and safety-critical careers.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {communityGroups.map((group) => (
              <article
                key={group.title}
                className="rounded-2xl bg-white border border-slate-200 p-7 shadow-sm"
              >
                <h3 className="font-display font-bold text-xl text-navy">
                  {group.title}
                </h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{group.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="rail-safety-education">
        <div className="container-rail">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="label-eyebrow text-safety">Age-appropriate learning</p>
              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Rail safety campaigns matched to age, maturity and real-world risk.
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                Young people need rail safety messages that match their stage of development.
                Younger pupils may need simple visual rules. Older students may need to explore
                peer pressure, social media dares, trespass, shortcuts, distraction and the
                consequences of unsafe choices.
              </p>
              <p className="mt-5 text-slate-600 leading-relaxed">
                UKTDA resources are designed to help schools deliver these messages through
                assemblies, lessons, worksheets, scenarios, quizzes, parent-facing materials
                and classroom discussion.
              </p>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {safetyAgeGroups.map((item) => (
                <article
                  key={`${item.age}-${item.title}`}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
                >
                  <p className="text-sm font-semibold text-safety">{item.age}</p>
                  <h3 className="mt-2 font-display font-bold text-lg text-navy">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {item.text}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-navy text-white" data-testid="resources-included">
        <div className="container-rail">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="label-eyebrow text-safety">What is included</p>
              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl leading-tight">
                Classroom-ready rail safety resources with no specialist knowledge required.
              </h2>
              <p className="mt-5 text-white/75 leading-relaxed">
                The resources are designed to make delivery simple. Teachers and youth leaders
                can use them for assemblies, form-time activities, PSHE lessons, citizenship
                sessions, careers events, youth group evenings and community safety campaigns.
              </p>
              <p className="mt-5 text-white/75 leading-relaxed">
                Schools can use the resources as stand-alone rail safety lessons or as part of
                a wider safety, behaviour, safeguarding, citizenship or rail careers programme.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {resourceTypes.map((resource) => (
                <div
                  key={resource}
                  className="rounded-xl border border-white/15 bg-white/5 px-5 py-4"
                >
                  <p className="font-semibold text-white">{resource}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="decision-making-focus">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">From awareness to judgement</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Rail safety education should not stop at telling young people what the rules are.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Many unsafe acts around the railway are linked to judgement, distraction,
              pressure, curiosity or a belief that “it will be fine this time”. UKTDA
              Communities focuses on helping learners think through risk, consequences
              and safer choices.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {professionalThemes.map((theme) => (
              <article
                key={theme.title}
                className="rounded-2xl bg-slate-50 border border-slate-200 p-6"
              >
                <h3 className="font-display font-bold text-lg text-navy">
                  {theme.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {theme.text}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50" data-testid="community-principles">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Our approach</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Useful, accessible and genuinely free at the point of use.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              The schools and youth resources within UKTDA Communities are built around
              practical value. The priority is to make rail safety education easier to deliver,
              easier to understand and easier to revisit.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <article className="rounded-2xl bg-white border border-slate-200 p-7">
              <h3 className="font-display font-bold text-xl text-navy">
                Free access
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Core school and youth rail safety resources are available without cost and
                without unnecessary barriers for teachers, youth leaders or community partners.
              </p>
            </article>

            <article className="rounded-2xl bg-white border border-slate-200 p-7">
              <h3 className="font-display font-bold text-xl text-navy">
                Age-appropriate design
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Resources reflect how young people think, behave and make decisions at
                different stages of development.
              </p>
            </article>

            <article className="rounded-2xl bg-white border border-slate-200 p-7">
              <h3 className="font-display font-bold text-xl text-navy">
                Real-world relevance
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Learning connects to real railway risk, including trespass, shortcuts,
                distraction, level crossings, platforms and peer influence.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white" data-testid="professional-community">
        <div className="container-rail">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="label-eyebrow text-safety">Railway careers and professional learning</p>
              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Helping older learners connect rail safety with railway careers.
              </h2>
            </div>

            <div className="space-y-5 text-slate-600 leading-relaxed">
              <p>
                For older students, rail safety education can also open a useful conversation
                about railway careers, safety-critical behaviour and professional standards.
              </p>
              <p>
                UKTDA Communities introduces ideas such as operational judgement, personal
                responsibility, communication, risk awareness and the importance of making
                safe decisions in controlled environments.
              </p>
              <p>
                This supports learners who may later explore railway careers while keeping
                the main purpose clear: safer behaviour around the railway.
              </p>
            </div>
          </div>

          <div className="mt-12 rounded-3xl bg-slate-50 border border-slate-200 p-8 md:p-10">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-display font-bold text-xl text-navy">
                  Aspiring drivers
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Understand the safety-critical behaviours, judgement and professionalism
                  expected before entering the railway.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-xl text-navy">
                  Future rail workers
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Explore how risk awareness, communication and responsibility matter in
                  operational, customer-facing and safety-critical rail roles.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-xl text-navy">
                  Schools and careers leads
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Use rail safety as a practical route into discussions about employability,
                  behaviour, teamwork and safety culture.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="rail-safety-faq"
        className="py-20 md:py-28 bg-slate-50"
        data-testid="rail-safety-faq"
      >
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Rail safety education FAQ</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Frequently asked questions about free rail safety and careers resources.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              These questions are designed to help individuals, schools, colleges, youth
              organisations, parents and community groups understand how UKTDA rail safety
              and career resources can be used.
            </p>
          </div>

          <div className="mt-12 space-y-5">
            {faqs.map((faq) => (
              <article
                key={faq.question}
                className="rounded-2xl bg-white border border-slate-200 p-6 md:p-7 shadow-sm"
              >
                <h3 className="font-display font-bold text-xl text-navy">
                  {faq.question}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Free resources"
        title="Use UKTDA rail safety and rail careers resources with your school, college or community."
        intro="If you are an individual learner, teacher, careers lead, youth leader, parent, rail professional or community organisation, UKTDA Communities provides practical resources to support safer behaviour and stronger railway career awareness."
        primary={{ label: "Request free resources", to: "/contact", testId: "communities-cta-contact" }}
        secondary={{ label: "Explore UKTDA training", to: "/training", testId: "communities-cta-training" }}
      />
    </>
  );
}